import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Table, Tag, Button, Tooltip, Spin, message, Modal, Row, Col } from "antd";
import {
  ArrowLeftOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  DollarOutlined,
  CalendarOutlined,
  CommentOutlined,
  EditOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { ViewJobDetailsWrapper } from "./ViewJobDetails.style";
import axios from "axios";
import { API_CONST } from "../../../const";

const CustomerJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_JOB_REQUIREMENT}/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.job) {
          setJobData(response.data.job);
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
        message.error("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };

    const fetchSubmittedProfiles = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.post(
          API_CONST.GET_JOB_APPLICATIONS,
          { jobId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.applications) {
          const mappedProfiles = response.data.applications.map((app) => {
            return {
              id: app.applicationId,
              talentId: app.talentId,
              name: app.talent?.name || 'Unknown',
              role: app.talent?.role || 'N/A',
              primarySkills: app.talent?.primarySkills || [],
              monthlyRate: formatCurrency(app.talent?.monthlyRate),
              experience: app.talent?.experience || 'N/A',
              noticePeriod: app.talent?.noticePeriod || 'N/A',
              resumeUrl: app.talent?.resumeUrl,
              status: app.status,
              submittedAt: app.submittedAt,
            };
          });
          setProfiles(mappedProfiles);
        }
      } catch (error) {
        console.error("Error fetching submitted profiles:", error);
      }
    };

    if (jobId) {
      fetchJobDetails();
      fetchSubmittedProfiles();
    }
  }, [jobId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `₹ ${Number(amount).toLocaleString('en-IN')}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'green';
      case 'closed': return 'red';
      case 'filled': return 'blue';
      case 'draft': return 'orange';
      default: return 'default';
    }
  };

  const handleScheduleInterview = (record) => {
    Modal.confirm({
      title: "Schedule Interview",
      content: `Are you sure you want to schedule an interview for ${record.name}?`,
      okText: "Schedule",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.UPDATE_JOB_APPLICATION}/${record.id}`,
            { status: "interview_scheduled" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setProfiles(
            profiles.map((p) =>
              p.id === record.id ? { ...p, status: "interview_scheduled" } : p
            )
          );
          message.success(`Interview scheduled for ${record.name}`);
        } catch (error) {
          console.error("Error scheduling interview:", error);
          message.error("Failed to schedule interview");
        }
      },
    });
  };

  const handleDownloadResume = async (resumeUrl, name) => {
    if (!resumeUrl) {
      message.warning("No resume available for this profile");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: resumeUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank");
      } else {
        message.error("Could not get download URL");
      }
    } catch (error) {
      console.error("Error downloading resume:", error);
      message.error("Failed to download resume");
    }
  };

  const columns = [
    {
      title: "Resume",
      dataIndex: "resumeUrl",
      key: "resume",
      width: 80,
      render: (url, record) => (
        <Tooltip title="View Resume">
          <FilePdfOutlined
            style={{ fontSize: 24, color: url ? "#ff4d4f" : "#d9d9d9", cursor: url ? "pointer" : "not-allowed" }}
            onClick={() => url && handleDownloadResume(url, record.name)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (name, record) => (
        <span 
          style={{ fontWeight: 500, color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/customer/talent-details/${record.talentId}`, {
            state: { from: `/customer/view-job/${jobId}`, fromLabel: "Back to Job Details" }
          })}
        >
          {name}
        </span>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
    },
    {
      title: "Primary Skills",
      dataIndex: "primarySkills",
      key: "primarySkills",
      width: 300,
      render: (skills) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(skills || []).map((skill, index) => (
            <Tag key={index} color="cyan" style={{ margin: "2px 0" }}>
              {typeof skill === 'object' ? `${skill.skill} - ${skill.level}` : skill}
            </Tag>
          ))}
          {(!skills || skills.length === 0) && <span style={{ color: "#8c8c8c" }}>-</span>}
        </div>
      ),
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: 120,
      render: (rate) => <span style={{ fontWeight: 500 }}>{rate || '-'}</span>,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 100,
    },
    {
      title: "Notice Period",
      dataIndex: "noticePeriod",
      key: "noticePeriod",
      width: 120,
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => {
        const isInterviewScheduled = record.status === "interview_scheduled";
        const isHired = record.status === "hired";
        const isRejected = record.status === "rejected";
        
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Tooltip title={isInterviewScheduled ? "Interview Already Scheduled" : "Schedule Interview"}>
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                size="small"
                disabled={isInterviewScheduled || isHired || isRejected}
                onClick={() => handleScheduleInterview(record)}
              />
            </Tooltip>
            <Tooltip title="Download Resume">
              <Button
                icon={<DownloadOutlined />}
                size="small"
                disabled={!record.resumeUrl}
                onClick={() => handleDownloadResume(record.resumeUrl, record.name)}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <ViewJobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading job details...</p>
        </div>
      </ViewJobDetailsWrapper>
    );
  }

  if (!jobData) {
    return (
      <ViewJobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Job not found</h2>
          <Button type="primary" onClick={() => navigate("/customer/my-jobs")}>
            Back to My Jobs
          </Button>
        </div>
      </ViewJobDetailsWrapper>
    );
  }

  return (
    <ViewJobDetailsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/customer/my-jobs")}
          className="back-button"
        >
          Back to My Jobs
        </Button>
        <div className="title-row">
          <h2>Job Details</h2>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/customer/edit-job/${jobId}`)}
            style={{
              backgroundColor: "#00d9a9",
              borderColor: "#00d9a9",
              height: "40px",
            }}
          >
            Edit Job
          </Button>
        </div>
      </div>

      <Card className="job-details-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="job-id">Job ID: {jobData.id}</div>
            <h1 className="job-title">{jobData.role}</h1>
            <div className="company-info">
              <Tag color={getStatusColor(jobData.status)}>{jobData.status?.toUpperCase()}</Tag>
              <Tag color="blue">{jobData.engagementType}</Tag>
              <Tag color="cyan" icon={<EnvironmentOutlined />}>{jobData.reportingLocation}</Tag>
              <Tag color="green" icon={<TeamOutlined />}>Requirements: {jobData.numberOfRequirements}</Tag>
            </div>
          </div>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/customer/job-full-details/${jobId}`)}
            style={{ 
              color: "#1890ff", 
              fontWeight: 500,
              fontSize: 14,
              padding: 0,
            }}
          >
            Detailed View
          </Button>
        </div>

        <div className="metrics-section" style={{ marginTop: 24 }}>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={6}>
              <div className="metric-box">
                <div className="metric-icon" style={{ background: "#e6f7ff" }}>
                  <DollarOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                </div>
                <div className="metric-info">
                  <h3>{formatCurrency(jobData.budget)}</h3>
                  <p>{jobData.budgetPeriod || 'Per Month'}</p>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <div className="metric-box">
                <div className="metric-icon" style={{ background: "#fff7e6" }}>
                  <CalendarOutlined style={{ fontSize: 24, color: "#faad14" }} />
                </div>
                <div className="metric-info">
                  <h3>{jobData.monthsOfEngagement || '-'}</h3>
                  <p>Engagement Duration</p>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <div className="metric-box">
                <div className="metric-icon" style={{ background: "#f6ffed" }}>
                  <ClockCircleOutlined style={{ fontSize: 24, color: "#52c41a" }} />
                </div>
                <div className="metric-info">
                  <h3>{jobData.experienceRange || '-'}</h3>
                  <p>Experience Required</p>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <div className="metric-box">
                <div className="metric-icon" style={{ background: "#fff1f0" }}>
                  <CommentOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
                </div>
                <div className="metric-info">
                  <h3>{jobData.communication || '-'}</h3>
                  <p>Communication Level</p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <Row gutter={[24, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Primary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.primarySkills || []).map((skill, index) => (
                  <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Secondary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.secondarySkills || []).map((skill, index) => (
                  <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
                {(!jobData.secondarySkills || jobData.secondarySkills.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Start Date:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.tentativeStartDate)}</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Created On:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.createdAt)}</div>
            </div>
          </Col>
        </Row>
      </Card>

      <div className="profiles-table-card">
        <h3>Submitted Profiles ({profiles.length})</h3>
        {profiles.length > 0 ? (
          <Table
            columns={columns}
            dataSource={profiles}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            scroll={{ x: 1200 }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#8c8c8c" }}>
            <p>No profiles submitted yet for this job.</p>
          </div>
        )}
      </div>
    </ViewJobDetailsWrapper>
  );
};

export default CustomerJobDetails;
